import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { EmptyState, ErrorNotice } from '../components/Feedback';
import { SenoIcon } from '../components/SenoIcon';
import { get, post } from '../lib/api';
import type { Chapter, ContentBlock, Section } from '../types';

type ExportRow = {
  id: string;
  artifact_id: string | null;
  format: 'docx' | 'pdf';
  scope: string;
  status: string;
  file_name: string | null;
  created_at: string;
};

type Attachment = {
  id: string;
  name: string;
  mimeType: string;
  category: string;
  status: string;
};
type PreflightIssue = {
  code: string;
  category: string;
  severity: 'error' | 'warning';
  message: string;
};
type Preflight = {
  ready: boolean;
  issues: PreflightIssue[];
  summary: {
    errors: number;
    warnings: number;
    citations: number;
    evidence: number;
    sections: number;
  };
};
type JournalCandidate = {
  id: string;
  candidate: { text?: string };
  validation: { issues?: Array<{ code: string; message: string; severity: string }> };
  status: string;
  baseRevision: number;
};
type JournalSection = {
  id: string;
  key: string;
  title: string;
  position: number;
  content: { text?: string };
  revision: number;
  status: string;
  candidates: JournalCandidate[];
};
type JournalGuideline = {
  id: string;
  attachmentId: string;
  rules: Record<string, unknown>;
  confidence: number;
  status: string;
};
type JournalProject = {
  id: string;
  entryPath: 'active_project' | 'uploaded_thesis';
  title: string;
  extractedFacts: Record<string, unknown>;
  confirmationStatus: 'draft' | 'confirmed';
  sections: JournalSection[];
  guidelines: JournalGuideline[];
};

export function FinalPage({ projectId }: { projectId: string }) {
  const [exports, setExports] = useState<ExportRow[]>([]);
  const [preflight, setPreflight] = useState<Preflight>({
    ready: false,
    issues: [],
    summary: { errors: 0, warnings: 0, citations: 0, evidence: 0, sections: 0 }
  });
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [document, setDocument] = useState<{
    chapters: Chapter[];
    sections: Section[];
    blocks: ContentBlock[];
  }>({ chapters: [], sections: [], blocks: [] });
  const [journals, setJournals] = useState<JournalProject[]>([]);
  const [selectedAttachment, setSelectedAttachment] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reviewBlockId, setReviewBlockId] = useState('');
  const [reviewResult, setReviewResult] = useState('');
  const [deepReview, setDeepReview] = useState('');
  const [journalGuidelineFile, setJournalGuidelineFile] = useState<Record<string, string>>({});
  const [journalCandidateEdits, setJournalCandidateEdits] = useState<Record<string, string>>({});
  const [pdfAvailable, setPdfAvailable] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    try {
      const [exportData, preflightData, files, doc, journalData] = await Promise.all([
        get<{ exports: ExportRow[] }>(`/api/projects/${projectId}/exports`),
        get<Preflight>(`/api/projects/${projectId}/preflight`),
        get<{ files: Attachment[] }>(`/api/projects/${projectId}/attachments`),
        get<{ chapters: Chapter[]; sections: Section[]; blocks: ContentBlock[] }>(
          `/api/projects/${projectId}/document`
        ),
        get<{ journals: JournalProject[] }>(`/api/projects/${projectId}/journals`)
      ]);
      setExports(exportData.exports);
      setPreflight(preflightData);
      setAttachments(files.files);
      setDocument(doc);
      setJournals(journalData.journals);
      setSelectedChapter(
        (current) =>
          current ||
          doc.chapters.find((chapter) => chapter.kind === 'body')?.id ||
          doc.chapters[0]?.id ||
          ''
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Finalisasi tidak dapat dimuat.');
    }
  }, [projectId]);

  useEffect(() => void load(), [load]);

  useEffect(() => {
    let active = true;
    void get<{ capabilities?: { pdf?: boolean } }>('/api/health')
      .then((health) => {
        if (active) setPdfAvailable(Boolean(health.capabilities?.pdf));
      })
      .catch(() => {
        if (active) setPdfAvailable(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const selectedChapterSections = useMemo(
    () => document.sections.filter((section) => section.chapterId === selectedChapter),
    [document.sections, selectedChapter]
  );
  const thesisSourceAttachments = useMemo(
    () =>
      attachments.filter(
        (file) =>
          file.status === 'stored' &&
          file.category === 'thesis-source' &&
          (file.mimeType.includes('pdf') || file.mimeType.includes('word'))
      ),
    [attachments]
  );
  const journalGuidelineAttachments = useMemo(
    () =>
      attachments.filter(
        (file) =>
          file.status === 'stored' &&
          file.category === 'journal-guideline' &&
          (file.mimeType.includes('pdf') || file.mimeType.includes('word'))
      ),
    [attachments]
  );

  async function compile(
    format: 'docx' | 'pdf',
    scope: 'chapter' | 'through-chapter' | 'proposal' | 'full' | 'selection',
    chapterId: string | null = null,
    selectionIds: string[] = []
  ) {
    setWorking(`${format}-${scope}`);
    setError('');
    try {
      const result = await post<{ reused: boolean }>(`/api/projects/${projectId}/exports`, {
        format,
        scope,
        chapterId,
        selectionIds
      });
      setNotice(
        result.reused
          ? 'Versi yang sama digunakan kembali dari cache aman.'
          : 'Dokumen berhasil dikompilasi.'
      );
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Dokumen belum berhasil dibuat.');
    } finally {
      setWorking('');
    }
  }

  async function createJournal(entryPath: 'active_project' | 'uploaded_thesis') {
    setWorking(`journal-${entryPath}`);
    setError('');
    try {
      await post(`/api/projects/${projectId}/journals`, {
        entryPath,
        sourceProjectId: entryPath === 'active_project' ? projectId : null,
        sourceAttachmentId: entryPath === 'uploaded_thesis' ? selectedAttachment : null,
        title: '',
        settings: { format: 'general-journal' }
      });
      setNotice(
        'Workspace artikel jurnal dibuat. Fakta hasil upload harus dikonfirmasi sebelum AI menyusun artikel.'
      );
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Draft jurnal belum dapat dibuat.');
    } finally {
      setWorking('');
    }
  }

  async function confirmJournalFacts(journalId: string) {
    setWorking(`journal-confirm-${journalId}`);
    setError('');
    try {
      await post(`/api/projects/${projectId}/journals/${journalId}/confirm`);
      setNotice('Fakta dasar artikel dikonfirmasi.');
      await load();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Fakta artikel belum dapat dikonfirmasi.'
      );
    } finally {
      setWorking('');
    }
  }

  async function uploadJournalGuideline(journalId: string, event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const file = form.get('journalGuideline');
    if (!(file instanceof File) || file.size <= 0) return;
    setWorking(`journal-guideline-${journalId}`);
    setError('');
    try {
      const upload = new FormData();
      upload.set('file', file);
      upload.set('category', 'journal-guideline');
      upload.set('aiReadable', 'true');
      const stored = await post<{ file: { id: string } }>(
        `/api/projects/${projectId}/attachments`,
        upload
      );
      await post(`/api/projects/${projectId}/journals/${journalId}/guidelines/extract`, {
        attachmentId: stored.file.id
      });
      formElement.reset();
      setNotice(
        'Author guideline dibaca sebagai candidate. Tinjau lalu konfirmasi sebelum dipakai.'
      );
      await load();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Author guideline belum berhasil dibaca.'
      );
    } finally {
      setWorking('');
    }
  }

  async function extractExistingJournalGuideline(journalId: string) {
    const attachmentId = journalGuidelineFile[journalId];
    if (!attachmentId) return;
    setWorking(`journal-guideline-${journalId}`);
    setError('');
    try {
      await post(`/api/projects/${projectId}/journals/${journalId}/guidelines/extract`, {
        attachmentId
      });
      setNotice('Author guideline dibaca sebagai candidate.');
      await load();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Author guideline belum berhasil dibaca.'
      );
    } finally {
      setWorking('');
    }
  }

  async function confirmJournalGuideline(journalId: string, guidelineId: string) {
    setWorking(`journal-guideline-confirm-${guidelineId}`);
    setError('');
    try {
      await post(
        `/api/projects/${projectId}/journals/${journalId}/guidelines/${guidelineId}/confirm`
      );
      setNotice('Author guideline aktif untuk artikel ini.');
      await load();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Author guideline belum dapat dikonfirmasi.'
      );
    } finally {
      setWorking('');
    }
  }

  async function generateJournalSection(journalId: string, section: JournalSection) {
    setWorking(`journal-section-${section.id}`);
    setError('');
    try {
      await post(
        `/api/projects/${projectId}/journals/${journalId}/sections/${section.id}/candidate`,
        {
          instruction: `Susun ${section.title} berdasarkan penelitian yang sudah dikonfirmasi. Jaga gaya akademik natural dan gunakan sumber nyata proyek.`,
          complexity: section.key === 'results_discussion' ? 7 : 5,
          idempotencyKey: `journal-${journalId}-${section.id}-${section.revision}-${crypto.randomUUID()}`
        }
      );
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Bagian artikel belum berhasil disusun.');
    } finally {
      setWorking('');
    }
  }

  async function applyJournalSection(
    journalId: string,
    section: JournalSection,
    candidate: JournalCandidate
  ) {
    setWorking(`journal-apply-${section.id}`);
    setError('');
    try {
      await post(`/api/projects/${projectId}/journals/${journalId}/sections/${section.id}/apply`, {
        candidateId: candidate.id,
        expectedRevision: section.revision,
        proposedText: journalCandidateEdits[candidate.id] ?? candidate.candidate.text ?? ''
      });
      setNotice(`${section.title} diterapkan ke artikel.`);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Usulan artikel belum dapat diterapkan.');
    } finally {
      setWorking('');
    }
  }

  async function exportJournal(journalId: string, format: 'docx' | 'pdf') {
    setWorking(`journal-export-${journalId}-${format}`);
    setError('');
    try {
      await post(`/api/projects/${projectId}/journals/${journalId}/export`, { format });
      setNotice(`Artikel ${format.toUpperCase()} berhasil dibuat.`);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Artikel belum berhasil diekspor.');
    } finally {
      setWorking('');
    }
  }

  async function reviewOriginality() {
    if (!reviewBlockId) return;
    const block = document.blocks.find((item) => item.id === reviewBlockId);
    if (!block) return;
    setWorking('originality');
    setError('');
    try {
      const result = await post<{ candidate: { status: string; candidate: { text: string } } }>(
        `/api/projects/${projectId}/ai-candidates`,
        {
          taskType: 'final_academic_review',
          targetType: 'block',
          targetId: block.id,
          instruction:
            'Tinjau kedekatan frasa dengan sumber, repetisi, kejelasan sitasi, dan kealamian gaya akademik. Jangan menjanjikan skor similarity atau mengubah fakta.',
          relevantObjectIds: [block.id],
          idempotencyKey: `originality-${block.id}-${block.revision}-${crypto.randomUUID()}`
        }
      );
      setReviewResult(result.candidate.candidate.text);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Originality Review belum berhasil.');
    } finally {
      setWorking('');
    }
  }

  async function runDeepPreflight() {
    setWorking('deep-preflight');
    setError('');
    try {
      const result = await post<{ review: string }>(`/api/projects/${projectId}/preflight/ai`);
      setDeepReview(result.review);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Audit akademik belum berhasil.');
    } finally {
      setWorking('');
    }
  }

  function toggleSection(sectionId: string) {
    setSelectedSections((current) =>
      current.includes(sectionId)
        ? current.filter((id) => id !== sectionId)
        : [...current, sectionId]
    );
  }

  return (
    <div className="content-page final-page">
      <div className="content-heading">
        <div>
          <span className="eyebrow">Periksa, lihat, unduh</span>
          <h1>Finalisasi</h1>
          <p>Format dan penomoran dihitung dari struktur dokumen. AI tidak menentukan layout.</p>
        </div>
      </div>
      {error ? <ErrorNotice message={error} /> : null}
      {notice ? <p className="success-note">{notice}</p> : null}

      <section className="validation-summary">
        <div className="section-title">
          <div>
            <span className="eyebrow">Final Preflight</span>
            <h2>Pemeriksaan kesiapan dokumen</h2>
            <p>
              Fakta, struktur, format, sumber, evidence, dan konsistensi diperiksa sebelum final.
            </p>
          </div>
          <span
            className={`health-score ${
              preflight.summary.errors === 0 && preflight.summary.warnings === 0
                ? 'healthy'
                : 'warning'
            }`}
          >
            {preflight.summary.errors > 0
              ? `Belum siap teknis · ${preflight.summary.errors} error`
              : preflight.summary.warnings > 0
                ? 'Teknis siap · akademik perlu tinjauan'
                : 'Siap teknis & akademik'}
          </span>
        </div>
        <div className="preflight-metrics">
          <span>{preflight.summary.sections} bagian</span>
          <span>{preflight.summary.citations} sitasi</span>
          <span>{preflight.summary.evidence} evidence</span>
        </div>
        {preflight.issues.length ? (
          <div className="preflight-list">
            {preflight.issues.map((issue) => (
              <article key={`${issue.code}-${issue.message}`} className={issue.severity}>
                <SenoIcon name={issue.severity === 'error' ? 'validasi' : 'konsistensi'} />
                <div>
                  <strong>{issue.category}</strong>
                  <p>{issue.message}</p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p>Tidak ada masalah teknis yang terdeteksi.</p>
        )}
        <div className="row-actions">
          <button
            className="button button-secondary"
            disabled={Boolean(working)}
            onClick={runDeepPreflight}
          >
            <SenoIcon name="ai" />{' '}
            {working === 'deep-preflight' ? 'Mengaudit…' : 'Audit akademik dengan Seno'}
          </button>
        </div>
        {deepReview ? (
          <div className="review-result">
            <strong>Audit akademik</strong>
            <p>{deepReview}</p>
          </div>
        ) : null}
      </section>

      <section className="golden-preview">
        <div className="section-title">
          <div>
            <span className="eyebrow">Golden Preview</span>
            <h2>Pratinjau struktur</h2>
            <p>Contoh layout sebelum file Word dibuat.</p>
          </div>
        </div>
        <div className="paper-preview">
          <div className="preview-cover">
            <small>SENOSKRIPSI</small>
            <h3>{document.chapters[0]?.title ?? 'Judul penelitian'}</h3>
            <p>Dokumen akademik terstruktur</p>
          </div>
          <div className="preview-page">
            {document.chapters.slice(0, 2).map((chapter) => (
              <div key={chapter.id}>
                <h4>
                  BAB {chapter.position} {chapter.title.toUpperCase()}
                </h4>
                {document.sections
                  .filter((section) => section.chapterId === chapter.id)
                  .slice(0, 2)
                  .map((section) => (
                    <div key={section.id}>
                      <h5>{section.title}</h5>
                      <p>
                        {String(
                          document.blocks.find((block) => block.sectionId === section.id)?.content
                            .text ?? 'Isi bagian akan tampil sesuai pedoman aktif.'
                        )}
                      </p>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="originality-review">
        <div className="section-title">
          <div>
            <span className="eyebrow">Originality Review</span>
            <h2>Tinjau kedekatan bahasa</h2>
            <p>
              Fokus pada kualitas parafrase, sitasi, dan repetisi—bukan “bypass” alat pendeteksi.
            </p>
          </div>
        </div>
        <div className="originality-controls">
          <select value={reviewBlockId} onChange={(event) => setReviewBlockId(event.target.value)}>
            <option value="">Pilih paragraf</option>
            {document.blocks
              .filter((block) => block.type === 'paragraph')
              .map((block) => {
                const section = document.sections.find((item) => item.id === block.sectionId);
                return (
                  <option key={block.id} value={block.id}>
                    {section?.title ?? 'Bagian'} · Paragraf {block.position}
                  </option>
                );
              })}
          </select>
          <button
            className="button button-primary"
            disabled={!reviewBlockId || Boolean(working)}
            onClick={reviewOriginality}
          >
            <SenoIcon name="parafrase" /> Tinjau paragraf
          </button>
        </div>
        {reviewResult ? (
          <div className="review-result">
            <strong>Catatan review</strong>
            <p>{reviewResult}</p>
          </div>
        ) : null}
      </section>

      <section className="export-studio">
        <div className="section-title">
          <div>
            <span className="eyebrow">Document Compiler</span>
            <h2>Unduh draft kapan saja</h2>
            <p>Pilih BAB, sampai BAB tertentu, proposal, bagian pilihan, atau seluruh draft.</p>
            {pdfAvailable === false ? (
              <small>PDF belum tersedia di lingkungan ini. Ekspor DOCX tetap dapat digunakan.</small>
            ) : null}
          </div>
        </div>
        <div className="export-actions export-actions-complete">
          <div>
            <strong>Proposal BAB I–III</strong>
            <button
              className="button button-primary"
              disabled={Boolean(working)}
              onClick={() => compile('docx', 'proposal')}
            >
              <SenoIcon name="docx" /> DOCX
            </button>
            <button
              className="button button-secondary"
              disabled={Boolean(working) || pdfAvailable !== true}
              title={pdfAvailable === false ? 'PDF renderer belum dikonfigurasi.' : undefined}
              onClick={() => compile('pdf', 'proposal')}
            >
              <SenoIcon name="pdf" /> PDF
            </button>
          </div>
          <div>
            <strong>Seluruh draft</strong>
            <button
              className="button button-primary"
              disabled={Boolean(working)}
              onClick={() => compile('docx', 'full')}
            >
              <SenoIcon name="docx" /> DOCX
            </button>
            <button
              className="button button-secondary"
              disabled={Boolean(working) || pdfAvailable !== true}
              title={pdfAvailable === false ? 'PDF renderer belum dikonfigurasi.' : undefined}
              onClick={() => compile('pdf', 'full')}
            >
              <SenoIcon name="pdf" /> PDF
            </button>
          </div>
          <div className="export-custom-card">
            <strong>BAB tertentu</strong>
            <select
              value={selectedChapter}
              onChange={(event) => {
                setSelectedChapter(event.target.value);
                setSelectedSections([]);
              }}
            >
              {document.chapters.map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  BAB {chapter.position} · {chapter.title}
                </option>
              ))}
            </select>
            <div className="row-actions compact-actions">
              <button
                className="button button-secondary"
                disabled={!selectedChapter || Boolean(working)}
                onClick={() => compile('docx', 'chapter', selectedChapter)}
              >
                <SenoIcon name="docx" /> BAB ini
              </button>
              <button
                className="button button-secondary"
                disabled={!selectedChapter || Boolean(working)}
                onClick={() => compile('docx', 'through-chapter', selectedChapter)}
              >
                <SenoIcon name="download" /> Sampai BAB ini
              </button>
            </div>
          </div>
          <div className="export-custom-card">
            <strong>Pilih bagian</strong>
            <div className="selection-list">
              {selectedChapterSections.map((section) => (
                <label key={section.id}>
                  <input
                    type="checkbox"
                    checked={selectedSections.includes(section.id)}
                    onChange={() => toggleSection(section.id)}
                  />{' '}
                  {section.title}
                </label>
              ))}
            </div>
            <button
              className="button button-secondary"
              disabled={!selectedSections.length || Boolean(working)}
              onClick={() => compile('docx', 'selection', selectedChapter, selectedSections)}
            >
              <SenoIcon name="docx" /> Unduh pilihan
            </button>
          </div>
        </div>
        {exports.length === 0 ? (
          <EmptyState
            title="Belum ada hasil ekspor"
            description="Buat draft DOCX kapan saja selama penyusunan."
          />
        ) : (
          <div className="export-history">
            {exports.map((item) => (
              <article key={item.id}>
                <SenoIcon name={item.format === 'pdf' ? 'pdf' : 'docx'} />
                <div>
                  <strong>{item.file_name ?? `${item.format.toUpperCase()} ${item.scope}`}</strong>
                  <span>{new Date(item.created_at).toLocaleString('id-ID')}</span>
                </div>
                {item.artifact_id ? (
                  <a
                    className="button button-secondary"
                    href={`/api/projects/${projectId}/exports/${item.artifact_id}/download`}
                  >
                    <SenoIcon name="download" /> Unduh
                  </a>
                ) : null}
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="journal-builder">
        <div className="section-title">
          <div>
            <span className="eyebrow">Artikel jurnal</span>
            <h2>Journal Builder</h2>
            <p>
              Bangun artikel dari fakta dan evidence penelitian yang sama, lalu sesuaikan author
              guideline tujuan.
            </p>
          </div>
        </div>
        <div className="journal-paths">
          <article>
            <SenoIcon name="jurnal" />
            <h3>Dari proyek aktif</h3>
            <p>Fakta confirmed/protected dari skripsi ini menjadi sumber artikel.</p>
            <button
              className="button button-primary"
              disabled={Boolean(working)}
              onClick={() => createJournal('active_project')}
            >
              Buat workspace jurnal
            </button>
          </article>
          <article>
            <SenoIcon name="docx" />
            <h3>Dari skripsi yang diupload</h3>
            <p>Pilih DOCX/PDF. Fakta hasil ekstraksi harus ditinjau sebelum penulisan.</p>
            <select
              value={selectedAttachment}
              onChange={(event) => setSelectedAttachment(event.target.value)}
            >
              <option value="">Pilih berkas</option>
              {thesisSourceAttachments.map((file) => (
                <option key={file.id} value={file.id}>
                  {file.name}
                </option>
              ))}
            </select>
            {thesisSourceAttachments.length === 0 ? (
              <small>
                Upload skripsi PDF/DOCX di Bahan → Berkas proyek dengan kategori “Skripsi sumber”.
              </small>
            ) : null}
            <button
              className="button button-secondary"
              disabled={!selectedAttachment || Boolean(working)}
              onClick={() => createJournal('uploaded_thesis')}
            >
              Analisis sebagai jurnal
            </button>
          </article>
        </div>

        {journals.length ? (
          <div className="journal-workspaces">
            {journals.map((journal, journalIndex) => {
              const candidateGuideline = journal.guidelines.find(
                (item) => item.status === 'candidate'
              );
              const confirmedGuideline = journal.guidelines.find(
                (item) => item.status === 'confirmed'
              );
              return (
                <article key={journal.id} className="journal-workspace-card">
                  <header>
                    <div>
                      <span className="eyebrow">Artikel {journalIndex + 1}</span>
                      <h3>{journal.title || 'Artikel dari penelitian'}</h3>
                    </div>
                    <span
                      className={`status-pill ${journal.confirmationStatus === 'confirmed' ? 'success' : 'warning'}`}
                    >
                      {journal.confirmationStatus === 'confirmed'
                        ? journal.entryPath === 'active_project'
                          ? 'Hanya fakta terkonfirmasi'
                          : 'Fakta upload dikonfirmasi'
                        : 'Perlu konfirmasi fakta'}
                    </span>
                  </header>

                  {journal.confirmationStatus !== 'confirmed' ? (
                    <div className="journal-facts-review">
                      <strong>Fakta hasil ekstraksi</strong>
                      <pre>{JSON.stringify(journal.extractedFacts, null, 2)}</pre>
                      <button
                        className="button button-primary"
                        disabled={Boolean(working)}
                        onClick={() => confirmJournalFacts(journal.id)}
                      >
                        <SenoIcon name="validasi" /> Konfirmasi fakta ini
                      </button>
                    </div>
                  ) : null}

                  <div className="journal-guideline-box">
                    <strong>Template / Author Guidelines</strong>
                    <p>
                      {confirmedGuideline
                        ? 'Author guideline sudah aktif.'
                        : 'Opsional, tetapi direkomendasikan bila jurnal tujuan sudah ditentukan.'}
                    </p>
                    <form
                      onSubmit={(event) => uploadJournalGuideline(journal.id, event)}
                      className="inline-upload-form"
                    >
                      <input type="file" name="journalGuideline" accept=".pdf,.docx" />
                      <button className="button button-secondary" disabled={Boolean(working)}>
                        <SenoIcon name="pedoman" /> Upload & baca
                      </button>
                    </form>
                    <div className="journal-existing-guideline">
                      <select
                        value={journalGuidelineFile[journal.id] ?? ''}
                        onChange={(event) =>
                          setJournalGuidelineFile((current) => ({
                            ...current,
                            [journal.id]: event.target.value
                          }))
                        }
                      >
                        <option value="">Atau pilih PDF/DOCX di Berkas</option>
                        {journalGuidelineAttachments.map((file) => (
                          <option key={file.id} value={file.id}>
                            {file.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="button button-secondary"
                        disabled={!journalGuidelineFile[journal.id] || Boolean(working)}
                        onClick={() => extractExistingJournalGuideline(journal.id)}
                      >
                        Baca guideline
                      </button>
                      {journalGuidelineAttachments.length === 0 ? (
                        <small>
                          Berkas yang dapat dipilih harus berkategori “Author guideline jurnal”.
                        </small>
                      ) : null}
                    </div>
                    {candidateGuideline ? (
                      <div className="guideline-candidate-review">
                        <pre>{JSON.stringify(candidateGuideline.rules, null, 2)}</pre>
                        <button
                          className="button button-primary"
                          disabled={Boolean(working)}
                          onClick={() => confirmJournalGuideline(journal.id, candidateGuideline.id)}
                        >
                          <SenoIcon name="validasi" /> Gunakan guideline ini
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <div className="journal-section-list">
                    {journal.sections.map((section) => {
                      const latest =
                        section.candidates.find((candidate) => candidate.status === 'valid') ??
                        section.candidates[0];
                      const candidateText = latest
                        ? (journalCandidateEdits[latest.id] ?? latest.candidate.text ?? '')
                        : '';
                      const hasBlockingCandidateIssue =
                        latest?.validation.issues?.some((issue) => issue.severity === 'error') ?? false;
                      return (
                        <section key={section.id} className="journal-section-editor">
                          <div className="journal-section-head">
                            <strong>{section.title}</strong>
                            <span>v{section.revision}</span>
                          </div>
                          <textarea
                            rows={5}
                            value={String(section.content.text ?? '')}
                            readOnly
                            placeholder="Bagian belum disusun."
                          />
                          {latest ? (
                            <div className="journal-candidate-preview">
                              <label>
                                Usulan Seno
                                <textarea
                                  rows={6}
                                  value={candidateText}
                                  onChange={(event) =>
                                    setJournalCandidateEdits((current) => ({
                                      ...current,
                                      [latest.id]: event.target.value
                                    }))
                                  }
                                />
                              </label>
                              {latest.validation.issues?.length ? (
                                <small>
                                  {latest.validation.issues
                                    .map((issue) => issue.message)
                                    .join(' · ')}
                                </small>
                              ) : null}
                            </div>
                          ) : null}
                          <div className="row-actions compact-actions">
                            {section.key !== 'bibliography' ? (
                              <button
                                className="button button-secondary"
                                disabled={
                                  journal.confirmationStatus !== 'confirmed' || Boolean(working)
                                }
                                onClick={() => generateJournalSection(journal.id, section)}
                              >
                                <SenoIcon name="ai" /> Susun usulan
                              </button>
                            ) : (
                              <span className="muted-note">
                                Daftar pustaka mengikuti Citation Engine.
                              </span>
                            )}
                            {latest?.status === 'valid' ? (
                              <button
                                className="button button-primary"
                                disabled={
                                  Boolean(working) ||
                                  !candidateText.trim() ||
                                  hasBlockingCandidateIssue
                                }
                                onClick={() => applyJournalSection(journal.id, section, latest)}
                              >
                                Terapkan
                              </button>
                            ) : null}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                  <div className="row-actions journal-export-actions">
                    <button
                      className="button button-primary"
                      disabled={Boolean(working)}
                      onClick={() => exportJournal(journal.id, 'docx')}
                    >
                      <SenoIcon name="docx" /> Unduh artikel DOCX
                    </button>
                    <button
                      className="button button-secondary"
                      disabled={Boolean(working) || pdfAvailable !== true}
                      title={pdfAvailable === false ? 'PDF renderer belum dikonfigurasi.' : undefined}
                      onClick={() => exportJournal(journal.id, 'pdf')}
                    >
                      <SenoIcon name="pdf" /> Buat PDF
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </section>
    </div>
  );
}
